import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Menu from 'apollo-react/components/Menu';
import MenuItem from 'apollo-react/components/MenuItem';
import ArrowDown from 'apollo-react-icons/ArrowDown';
import ArrowUp from 'apollo-react-icons/ArrowUp';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { UNSAVED_CONFIRM_MESSAGE } from '../../constants/Common';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { isFormLoading } from '../../form-designer/selectors/common.selectors';
import {
  anyFormHasSelectedSubGrids,
  formHasUnsavedChanges,
} from '../../form-designer/selectors/runtime.selectors';
import { getIsRtlLanguage } from '../../selectors/AuthSelectors';
import { getUITA } from '../../utils/commonUtils';

const useStyles = makeStyles({
  buttonIconSpace: {
    '& span svg:first-child': {
      marginLeft: 4,
    },
  },
  fullWidth: {
    width: '100%',
  },
  buttonTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  buttonTitleContainerInsidePopover: {
    justifyContent: 'space-between',
  },
  buttonText: {
    marginRight: 5,
  },
  buttonIcon: {
    display: 'flex',
    paddingTop: 1,
  },
  divider: {
    borderBottom: '1px solid #e9e9e9',
  },
  group: {
    fontWeight: 'bold',
    color: '#000000 !important',
  },
  item: {
    textIndent: '12px',
  },
  menuItem: {
    opacity: '1 !important',
    paddingBottom: '18px !important',
    paddingTop: '18px !important',
  },
  menuItemGroupDisabled: {
    opacity: '0.4 !important',
    paddingBottom: '18px !important',
    paddingTop: '18px !important',
  },
});

function MenuButton({ buttonText, menuItems = [], disabled, icon = null }) {
  const classes = useStyles();
  const translateString = useTranslation();
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const anyFormHasSubGrids = useSelector(anyFormHasSelectedSubGrids);
  const hasUnsavedChanges = useFormSelector(formHasUnsavedChanges, { formKey: FORM_KEYS.STEP });
  const stepFormIsMounted = !useSelector(isFormLoading(FORM_KEYS.STEP));
  const { isMobileDevice } = useWindowDimensions();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const isMinifiedVersion = anyFormHasSubGrids || isMobileDevice;

  const unsavedChangesConfirmation = () => {
    return (
      !stepFormIsMounted ||
      !hasUnsavedChanges ||
      window.confirm(translateString(UNSAVED_CONFIRM_MESSAGE))
    );
  };

  const handleClick = (ev) => {
    setOpen(true);
    setAnchorEl(ev.currentTarget);
  };

  const handleMenuClose = () => {
    setOpen(false);
  };

  const handleItemClick = (item) => () => {
    if (unsavedChangesConfirmation()) {
      item.onClick();
      handleMenuClose();
    }
  };

  return (
    <>
      <Button
        aria-owns={open ? 'simple-menu' : null}
        aria-haspopup="true"
        color="secondary"
        variant="secondary"
        onClick={handleClick}
        disabled={disabled}
        icon={!isMinifiedVersion && icon}
        className={classNames(
          isRtlLanguage ? classes.buttonIconSpace : '',
          isMinifiedVersion ? classes.fullWidth : ''
        )}
      >
        <span
          className={classNames(
            classes.buttonTitleContainer,
            isMinifiedVersion ? classes.buttonTitleContainerInsidePopover : ''
          )}
        >
          <span className={classes.buttonText}>{buttonText}</span>
          <span className={classes.buttonIcon}>
            {open ? <ArrowUp fontSize="extraSmall" /> : <ArrowDown fontSize="extraSmall" />}
          </span>
        </span>
      </Button>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        style={{ marginTop: 4 }}
        PaperProps={{
          style: {
            maxHeight: 378,
          },
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.id || item.text}
            className={classNames(classes.menuItem, {
              [classes.menuItemGroupDisabled]: item.isGroupHeader && item.isGroupEmpty,
              [classes.divider]: item.divider,
            })}
            title={item.isGroupHeader ? undefined : item.tooltip}
            disabled={item.disabled || item.isGroupHeader}
            onClick={handleItemClick(item)}
          >
            <span
              className={item.isGroupHeader ? classes.group : classes.item}
              ui-ta={getUITA('MenuButton', item.text)}
            >
              {item.text}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default MenuButton;
